import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BuildIcon from "@mui/icons-material/Build";
import GavelIcon from "@mui/icons-material/Gavel";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import MemberPicker from "./MemberPicker";
import NewWorkOrderForm from "./NewWorkOrderForm";
import AddViolationForm from "../MemberProfilePage/AddViolationForm";
import AddArchitecturalRequest from "../MemberProfilePage/AddArchitecturalRequest";
import api from "../../utils/api";

const REQUEST_TYPES = [
  { key: "workorder", label: "Green Sheet / Work Order", icon: BuildIcon, color: "#2f63ad" },
  { key: "violation", label: "Violation", icon: GavelIcon, color: "#b45309" },
  { key: "architectural", label: "Architectural Request", icon: ArchitectureIcon, color: "#6b46c1" },
];

const DIALOG_WIDTH = { workorder: "sm", violation: "md", architectural: "lg" };

// Dashboard "New Request" quick-create: pick a type, pick a member/property,
// then fill out the actual creation form right here — no navigation away
// from the Dashboard.
const NewRequestDialog = ({ open, onClose }) => {
  const [type, setType] = useState(null);
  const [member, setMember] = useState(null);
  const [violationTypes, setViolationTypes] = useState([]);

  useEffect(() => {
    if (type === "violation" && violationTypes.length === 0) {
      api.get("/getCcrViolationTypes").then((res) => setViolationTypes(res.data || [])).catch(() => {});
    }
  }, [type, violationTypes.length]);

  const reset = () => {
    setType(null);
    setMember(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const stepTitle = !type ? "New Request" : !member ? "Select Property" : REQUEST_TYPES.find((t) => t.key === type)?.label;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={type && member ? DIALOG_WIDTH[type] : "xs"} fullWidth
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {type && !member && (
            <IconButton size="small" onClick={() => setType(null)}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            {stepTitle}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, ...(type && member ? { p: 0 } : {}) }}>
        {!type && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pb: 1 }}>
            {REQUEST_TYPES.map((rt) => {
              const Icon = rt.icon;
              return (
                <Box key={rt.key} onClick={() => setType(rt.key)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5, p: 1.75, borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.08)", cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(47,99,173,0.05)", borderColor: rt.color },
                  }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: "10px", display: "flex", alignItems: "center",
                    justifyContent: "center", backgroundColor: `${rt.color}1a`,
                  }}>
                    <Icon sx={{ fontSize: 20, color: rt.color }} />
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                    {rt.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {type && !member && (
          <Box sx={{ pb: 1 }}>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
              Search for the member or property this request is for.
            </Typography>
            <MemberPicker selectedMember={member} onSelect={setMember} onClear={() => setMember(null)} />
          </Box>
        )}

        {type === "workorder" && member && (
          <Box sx={{ p: 3 }}>
            <NewWorkOrderForm
              propertyId={member.ClubMemberCode}
              onSuccess={handleClose}
              onCancel={() => setMember(null)}
            />
          </Box>
        )}

        {type === "violation" && member && (
          <AddViolationForm
            violationTypes={violationTypes}
            propertyId={member.ClubMemberCode}
            accountId={member.ClubMemberCode}
            onSuccess={handleClose}
          />
        )}

        {type === "architectural" && member && (
          <AddArchitecturalRequest
            propertyId={member.ClubMemberCode}
            accountId={member.ClubMemberCode}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewRequestDialog;
